import {
  Alert,
  Box,
  Button,
  FormField,
  SelectProps,
  SpaceBetween,
} from '@amzn/awsui-components-core';
import { getText } from '@amzn/awscat-editor';
import { Epic, Task } from '@amzn/awscat-pattern-library-service-model';
import React from 'react';
import { v4 as uuid } from 'uuid';
import constants from '../../../../common/constants';
import MultiselectCustomInput from '../../MultiselectCustomInput';
import { TaskSkillOptions } from '../../../utils/SelectableOptions';
import { TaskSkill } from '@amzn/awscat-pattern-library-service-model';
import { getTaskSkills, orderSkillOptions, validateSkills } from '../../../utils/TaskUtils';
import EditorWrapper from '../../../../editor/EditorWrapper';
import { TiptapOutputType } from '@amzn/awscat-editor';
import { TiptapMenuType } from '../../../../editor/utils';

interface Props {
  epics: Epic[];
  tasks: Task[];
  confirmAddTask?: (t: Task) => void;
}

interface State {
  title: TiptapOutputType;
  titleError: string;
  description: TiptapOutputType;
  skills: string;
  skillsError: string;
  htmlView: boolean;
  visible: boolean;
}

const initialState = {
  description: '',
  htmlView: false,
  skills: '',
  skillsError: '',
  title: '',
  titleError: '',
  visible: true,
};

class TaskCreatorForm extends React.Component<Props, State> {
  private taskSkillOptions: SelectProps.Option[];

  // @ts-expect-error TS7006 - strict migration
  constructor(props) {
    super(props);
    this.state = {
      ...initialState,
    };
    this.taskSkillOptions = orderSkillOptions(TaskSkillOptions);
  }

  public render() {
    const { titleError, title, description, skills, skillsError, htmlView, visible } = this.state;
    const columns = 2;
    const taskTitleEditor = (
      <EditorWrapper
        model={title}
        readOnly={false}
        onModelChange={this.updateTaskTitle}
        menuType={TiptapMenuType.Simple}
      />
    );
    const taskDescEditor = (
      <EditorWrapper
        model={description}
        readOnly={false}
        onModelChange={this.updateTaskDescription}
      />
    );

    const htmlViewAlert = htmlView ? (
      <Alert type="warning">{constants.ADD_TASK_WARNING_TEXT}</Alert>
    ) : null;
    return visible ? (
      <>
        <Box
          className="task-creator-container"
          padding="m"
          margin={{ left: 's', right: 's', top: 'l' }}
        >
          <SpaceBetween direction="vertical" size="l">
            <div className="task-creator-title">
              <FormField
                label={constants.TASK_TITLE_LABEL}
                errorText={titleError}
                stretch
                i18nStrings={{ errorIconAriaLabel: 'Error' }}
              >
                {taskTitleEditor}
              </FormField>
            </div>
            <MultiselectCustomInput
              label={constants.TASK_SKILLS_LABEL}
              customLabel={constants.TASK_SKILLS_CUSTOM_LABEL}
              values={skills}
              selectOptions={this.taskSkillOptions}
              onSelectsUpdated={this.updateTaskSkills}
              onCustomInputUpdated={this.updateTaskSkills}
              stretch
              placeholder={constants.TASK_SKILLS_PLACEHOLDER}
              customOption={TaskSkill.Other}
              validationError={skillsError}
            />
            <FormField
              label={constants.TASK_DESCRIPTION_LABEL}
              stretch
              i18nStrings={{ errorIconAriaLabel: 'Error' }}
            >
              <Box variant="div" padding={{ top: 's' }}>
                <div className="editorbox-small">{taskDescEditor}</div>
              </Box>
            </FormField>
            {htmlViewAlert}
            <Box margin={{ top: 'xs' }} float={'right'}>
              <Button variant={'link'} onClick={this.cancelTask}>
                {constants.CANCEL_BUTTON_TEXT}
              </Button>
              <Button disabled={htmlView} onClick={this.confirmAddTask}>
                {constants.CONFIRM_BUTTON_TEXT}
              </Button>
            </Box>
          </SpaceBetween>
        </Box>
        <hr className="task-creator-divider" />
      </>
    ) : null;
  }

  public componentDidUpdate = (prevProps: any, prevState: any) => {
    if (this.state.title !== prevState.title) {
      this.validateTitle();
    }
    if (this.state.skills !== prevState.skills) {
      this.validateSkills();
    }
  };

  private confirmAddTask = () => {
    const { title, skills, description } = this.state;
    const error = this.validateInput();

    if (!error && this.props.confirmAddTask) {
      this.props.confirmAddTask(
        Object.assign(new Task(), {
          description,
          id: uuid(),
          skills,
          title,
        }),
      );

      this.setState({
        visible: false,
      });
    }
  };

  private cancelTask = () => {
    this.setState({
      visible: false,
    });
  };

  private handleToggleView = () => {
    const { htmlView } = this.state;
    const newHtmlView = !htmlView;
    this.setState({
      htmlView: newHtmlView,
    });
  };

  private updateTaskSkills = (selectValues: string[], custValues: string) => {
    const skills = getTaskSkills(selectValues, custValues);
    this.setState({
      skills,
    });
  };

  private updateTaskDescription = (content: TiptapOutputType) => {
    this.setState({ description: content });
  };

  private updateTaskTitle = (content: TiptapOutputType) => {
    this.setState({ title: content });
  };

  private getPredecessorTasksSelectOptions = (epics: any[]) => {
    return epics.reduce((acc, epic) => {
      // @ts-expect-error TS7006 - strict migration
      const epicTasks = epic.tasks.map((task) => ({
        id: `${epic.title} - ${task.title}`,
        label: task.title,
      }));

      epicTasks.unshift({
        id: `${epic.title}`,
        label: epic.title,
      });

      if (epicTasks.length !== 0) {
        return [
          ...acc,
          {
            id: epic.title,
            label: epic.title,
            options: epicTasks,
          },
        ];
      }

      return acc;
    }, []);
  };

  private validateTitle = () => {
    const { title } = this.state;
    let error: string = '';

    if (!title || getText(title).length <= 0) {
      error = constants.TASK_TITLE_REQUIRED;
    } else if (getText(title).length > 300) {
      error = constants.TASK_TITLE_TOO_LONG;
    } else if (this.checkDuplicateTask(title)) {
      error = constants.DUPLICATE_TASK_TITLE;
    }

    this.setState({
      titleError: error,
    });

    return error;
  };

  private validateSkills = () => {
    const { skills } = this.state;
    const error = validateSkills(skills);
    this.setState({
      skillsError: error,
    });
    return error;
  };

  private validateInput = () => {
    let error = this.validateTitle();
    if (!error) {
      error = this.validateSkills();
    }
    return error;
  };

  private checkDuplicateTask = (title: TiptapOutputType) => {
    const { tasks } = this.props;
    return tasks.some((task) => getText(task.title) === getText(title));
  };
}

export default TaskCreatorForm;
